import { trackEvent, trackGA, trackCdp } from 'utilities/tracking';
import { toDinero } from 'utilities/currency';
import { ACTIVE_PROMOTIONS } from 'constants/promotions';

// This is the height of the sticky CTA header from public-web-nav
const STICKY_HEADER_OFFSET = 62;

const TRACKING_EVENT_TYPES = Object.freeze({
  UpsellsRendered: 'UpsellsRendered',
  ChangeAlternative: 'Change Alternative',
  ChangeAddOn: 'Change Add On',
});

const getAddOnByVariantId = (addOns, variantId) =>
  Object.values(addOns)
    .flat()
    .find(({ variant_id }) => variant_id.toString() === variantId.toString());

const getAlternativeUrl = (newSlug) => {
  const parts = window.location.pathname.split('/');
  parts[parts.length - 1] = newSlug;
  return parts.join('/');
};

const onPageView = (productData) => {
  trackGA('ec:addProduct', productData);
  trackGA('ec:setAction', 'detail');

  trackEvent({
    name: 'Product',
    value: 'View',
    params: productData,
  });

  trackCdp({
    name: 'View Product',
    params: {
      feature: 'shop',
      product: { ...productData },
    },
  });
};

const groupAndFlattenActivePromos = (filteredActivePromosEntries) => {
  const appliedPromosByGroup = filteredActivePromosEntries.reduce(
    (prev, promo) => {
      const memo = prev;
      const group = memo[promo[1].priority] || [];
      group.push(promo[0]);
      memo[promo[1].priority] = group;
      return memo;
    },
    {},
  );

  return Object.entries(appliedPromosByGroup)
    .sort(([indexA, _promoNameA], [indexB, _promoNameB]) => indexA - indexB)
    .map(([_index, promoName]) => promoName);
};

/**
 * Get eligible promotions for a given product slug.
 *
 * @param {string} productSlug - The product slug.
 * @return {string[]} Array of eligible promotions for the slug, sorted by priority.
 */
const eligibleProductPromotions = (productSlug) => {
  const filteredActivePromosEntries = Object.entries(ACTIVE_PROMOTIONS).filter(
    ([_promo, config]) => config.appliesTo.includes(productSlug),
  );

  return groupAndFlattenActivePromos(filteredActivePromosEntries);
};

/**
 * Get eligible promotions for a given order.
 *
 * @param {Object} orderData - Order data object.
 * @return {string[]} Array of eligible promotions for the order, sorted by priority.
 */
const eligibleOrderPromotions = (orderData) => {
  const cart = orderData.cart_summary || [];
  const cartSlugs = cart.map(({ product_variant: { slug } }) => slug);

  const cartContains = (slugsToCheck) => {
    const set = new Set(cartSlugs);
    return slugsToCheck.some((el) => set.has(el));
  };

  const filteredActivePromosEntries = Object.entries(ACTIVE_PROMOTIONS).filter(
    ([_promo, config]) => cartContains(config.appliesTo),
  );

  return groupAndFlattenActivePromos(filteredActivePromosEntries);
};

const getTopPromoEligibility = (
  promosByPriority,
  cartClient,
  topPromoEligibilityReceived,
) => {
  if (promosByPriority.length === 0) return;

  const topPriorityPromos = promosByPriority[0];

  cartClient.promotionEligibility(...topPriorityPromos).then((data) => {
    if (data.length === 0) {
      return;
    }

    let eligibility;
    if (topPriorityPromos.length === 1) {
      eligibility = data.find(({ name }) => name === topPriorityPromos[0]);
    } else {
      eligibility =
        data.find(({ name, eligible }) => {
          return topPriorityPromos.includes(name) && eligible;
        }) || data[0];
    }

    topPromoEligibilityReceived(eligibility);
  });
};

const trackViewCart = ({ orderNumber, trackingData }) => {
  trackCdp({
    name: 'View Cart',
    params: {
      feature: 'shop',
      order_number: orderNumber,
      product_data: trackingData,
    },
  });
};

const trackContinueToCart = ({ orderNumber, trackingData }) => {
  trackCdp({
    name: 'Continue To Cart',
    params: {
      feature: 'shop',
      order_number: orderNumber,
      product_data: trackingData,
    },
  });
};

const trackAddToCart = ({
  mainProductTrackingData,
  addOnsTrackingData = [],
  cartNumber,
  quantity,
}) => {
  const allTrackingData = [mainProductTrackingData, ...addOnsTrackingData];
  allTrackingData.forEach((productData) => {
    trackGA('ec:addProduct', productData);
    trackGA('ec:setAction', 'add');

    trackEvent({
      name: 'Cart',
      value: 'Add',
      params: { ...productData, cartNumber },
    });

    trackCdp({
      name: 'Add Product',
      params: {
        feature: 'shop',
        order_number: cartNumber,
        quantity,
        ...productData,
      },
    });
  });

  // GoogleTagManager
  const price = allTrackingData.reduce(
    (sum, p) => sum + (Number(p.price_with_vat) || Number(p.price)),
    0,
  );

  const data = {
    product_skus: allTrackingData.map((product) => product.sku).join(','),
    product_slugs: allTrackingData.map((product) => product.slug).join(','),
    state: 'add_to_cart',
    price: price.toFixed(2), // remove rounding errors after 2 decimal places
    quantity,
  };

  // Allow if OneTrust is not defined or if it is defined and tracking explicitly allowed
  if (
    Sq.GoogleTagManager?.enabled &&
    (!Sq.OneTrust || Sq.OneTrust.advertisingTrackingEnabled)
  ) {
    Sq.GoogleTagManager.track(data);
  }

  if (!Sq.OneTrust || Sq.OneTrust.advertisingTrackingEnabled) {
    window.martech.track({
      event: 'Add to Cart',
      ...data,
    });
  }
};

const selectionPrice = (currentProduct, currentProductAddOns, selection) => {
  const addOnsPrice = Object.values(selection.addOns).reduce(
    (sum, variantId) => {
      const selectedAddOn = getAddOnByVariantId(
        currentProductAddOns,
        variantId,
      );

      return sum.add(toDinero(selectedAddOn?.sale_price_unformatted || 0));
    },
    toDinero(0),
  );

  const selectionTotal = addOnsPrice
    .add(toDinero(currentProduct.sale_price_unformatted))
    .multiply(selection.quantity);

  return selectionTotal.toUnit();
};

const setFooterDisclaimer = (disclaimer) => {
  if (!disclaimer || !Sq.SqShopFooter) return;

  // Prevent duplicate disclaimers from being set
  if (Sq.SqShopFooter.disclaimers.includes(disclaimer)) return;

  Sq.SqShopFooter.disclaimers = [
    ...Sq.SqShopFooter.disclaimers,
    `${disclaimer}`,
  ];
};

export {
  STICKY_HEADER_OFFSET,
  TRACKING_EVENT_TYPES,
  getAddOnByVariantId,
  getAlternativeUrl,
  onPageView,
  eligibleOrderPromotions,
  eligibleProductPromotions,
  trackAddToCart,
  trackContinueToCart,
  trackViewCart,
  getTopPromoEligibility,
  selectionPrice,
  setFooterDisclaimer,
};
